import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useTarget } from '@epihub/web/src/hooks/useTarget';
import { FloatingDock } from '@epihub/web/src/tailwind/components/FloatingDock';
import { IconCalendar, IconHome, IconMenu } from '@tabler/icons-react';
import { bookCalendarRoute, homeChatRoute } from '@krishanmadan/core/src/routes/frontendRoutes';
import { isCurrentRoute, navigateTo } from '@epihub/web/src/routes/nav';
import { useLocation } from 'react-router';
import { ESupportedLanguages } from '@krishanmadan/core/src/model/const';
import { ActionIcon, Button, Drawer } from '@mantine/core';
import { useToggleable } from '@epihub/web/src/hooks/useToggleable';
import { typeName, typePosition } from '../lib/typers';
import { HTMLConst } from '../lib/HTMLConst';
import { reactSpringPortal } from '../lib/HTMLComp';
import { styled, useTheme } from '../theme/theme';
import { log } from '../_ctx/log';
import { useGlobalStore } from '../store/useGlobalStore';
import { assets } from '../assets';
import { updateLanguage } from '../lib/updateLanguage';
import { useSfyr } from '../_ctx/sfyr';
import { LottieUnderline } from './Lottie';
import { cssH4, cssH6 } from './css';
import { SocialBar } from './SocialBar';

type THeaderProps = {
  className?: string;
};

export const Header = ({ className }: THeaderProps) => {
  const theme = useTheme();
  const isMobile = theme.screen.width.px < 769;
  const [hideUnderline, setHideUnderline] = React.useState(false);

  const {
    Target: LottieUnderlineInTarget,
    measureRef: lottieUnderlineInTargetMeasureRef,
  } = useTarget(
    LottieUnderline,
    HTMLConst.headerUnderlineTarget,
    React.useCallback((rect: DOMRect) => ({
      position: isMobile ? 'absolute' : 'fixed',
      top: rect.y + 21,
      left: rect.left - 6,
      width: rect.width + 25,
      zIndex: 999999,
    }), [isMobile]),
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      try {
        typeName(HTMLConst.headerName, () => {
          typePosition(HTMLConst.headerPosition1, HTMLConst.headerUnderlineTarget, () => {
            lottieUnderlineInTargetMeasureRef.current();
          });
        });
        clearInterval(interval);
      } catch (e) {
        log.e('Header::typeName', e);
      }
    }, 1000);
  }, []);

  // Hide the underline on mobile when scrolling
  React.useEffect(() => {
    if (!isMobile) {
      return;
    }

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      setHideUnderline(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setHideUnderline(false);
        lottieUnderlineInTargetMeasureRef.current();
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      clearTimeout(scrollTimeout);
    };
  }, [isMobile, lottieUnderlineInTargetMeasureRef]);
  return (
    <Root className={className}>
      <Left>
        <ShadowContent>
          <ShadowContentTextContainer>
            <TextName>
              Krishan Marco Madan
            </TextName>
            <IntroContainer>
              <TextIntro>
                Senior Software Engineer
              </TextIntro>
            </IntroContainer>
          </ShadowContentTextContainer>
          <IconsContainer>
            <SocialBar />
          </IconsContainer>
        </ShadowContent>
        <TypedContent>
          <TextContainer>
            <TextName id={HTMLConst.headerName}></TextName>
            <IntroContainer>
              <TextIntro id={HTMLConst.headerPosition1}></TextIntro>
            </IntroContainer>
          </TextContainer>
        </TypedContent>
      </Left>
      <Right>
        <FloatingDockWrapper>
          <Dock />
        </FloatingDockWrapper>
      </Right>
      {!hideUnderline && (
        isMobile
          ? ReactDOM.createPortal(
            <LottieUnderlineInTarget color={[theme.legacy.colors.accent1]} />,
            reactSpringPortal,
          )
          : <LottieUnderlineInTarget color={[theme.legacy.colors.accent1]} />
      )}
    </Root>
  );
};

const Dock = () => {
  const sfyr = useSfyr();
  const location = useLocation();
  const language = useGlobalStore((store) => store.settings.language);

  const actionItems = [
    ...isCurrentRoute(homeChatRoute, location)
      ? [{
        title: sfyr('ui.{lang}.dock.open_calendar', {
          defaults: {
            'ui.{lang=en}.dock.open_calendar': 'Book a Call',
            'ui.{lang=it}.dock.open_calendar': 'Prenota un Appuntamento',
          },
        }),
        icon: <IconCalendar className='h-full w-full text-neutral-500 dark:text-neutral-300' />,
        onClick: () => {
          useGlobalStore.getState().settings.setCalendarDrawerOpen(true);
        },
      }]
      : [],
    ...isCurrentRoute(bookCalendarRoute, location)
      ? [{
        title: sfyr('ui.{lang}.dock.homepage', {
          defaults: {
            'ui.{lang=en}.dock.homepage': 'Homepage',
            'ui.{lang=it}.dock.homepage': 'Homepage',
          },
        }),
        icon: <IconHome className='h-full w-full text-neutral-500 dark:text-neutral-300' />,
        onClick: () => navigateTo(homeChatRoute),
      }]
      : [],
    {
      title: sfyr('ui.{lang}.dock.change_language', {
        defaults: {
          'ui.{lang=en}.dock.change_language': 'Change to Italian',
          'ui.{lang=it}.dock.change_language': 'Change to English',
        },
      }),
      icon: (
        <div
          className='h-full w-full text-neutral-500 dark:text-neutral-300'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {language === ESupportedLanguages.it
            ? <assets.FlagUS.Component />
            : <assets.FlagItaly.Component />}
        </div>
      ),
      onClick: () => {
        const next = language === ESupportedLanguages.it ? ESupportedLanguages.en : ESupportedLanguages.it;
        updateLanguage(next);
      },
    },
  ];

  const { val: mobileDrawerOpened, setTrue: openMobileDrawer, setFalse: closeMobileDrawer } = useToggleable(false);
  const theme = useTheme();
  if (theme.screen.width.px < 769) {
    return (
      <>
        <Drawer
          position='right'
          opened={mobileDrawerOpened}
          onClose={closeMobileDrawer}
          size='100vw'
        >
          <MobileDrawerContent>
            {actionItems.map((ai, idx) => (
              <Button
                key={idx}
                fullWidth
                variant='outline'
                color='#ffffff'
                leftSection={(
                  <div style={{ width: 24 }}>
                    {ai.icon}
                  </div>
                )}
                onClick={() => {
                  ai.onClick?.();
                  closeMobileDrawer();
                }}
              >
                {ai.title}
              </Button>
            ))}
          </MobileDrawerContent>
        </Drawer>
        <ActionIcon
          onClick={openMobileDrawer}
          styles={{
            root: {
              position: 'absolute',
              top: 0,
              right: 0,
            },
          }}
          size={48}
          variant='transparent'
          color='#ffffff'
        >
          <IconMenu />
        </ActionIcon>
      </>
    );
  }

  return (
    <FloatingDock
      desktopClassName={cssFloatingDock}
      tooltipPosition='bottom'
      items={actionItems}
    />
  );
};

const Root = styled.div`
  position: relative;
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Left = styled.div`
`;

const Right = styled.div`
  position: relative;
`;

const MobileDrawerContent = styled.div`
  gap: 12px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .mantine-Button-label {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const cssFloatingDock = 'floatingDock';

const FloatingDockWrapper = styled.div`
  // Reenable pointer events on the background so when the user scrolls they can still interact with the calendar
  pointer-events: auto;
  .floatingDock {
    height: 132px;
    padding: 0 48px;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const TypedContent = styled(Content)`
  position: absolute;
  top: 0;
`;

const ShadowContent = styled(Content)`
  position: relative;
  // Reenable pointer events on the background so when the user scrolls they can still interact with the calendar
  pointer-events: auto;
`;

const TextContainer = styled.div`
  flex-grow: 1;
`;

const ShadowContentTextContainer = styled(TextContainer)`
  visibility: hidden;
`;

const IconsContainer = styled.div`
  margin-top: 12px;
`;

const IntroContainer = styled.div`

`;

const Text = styled.h1`
  color: ${({ theme }) => theme.legacy.colors.primaryText};
  display: block;
  margin: 0;
  max-width: 87vw;
  text-transform: uppercase;
`;

const TextName = styled(Text)`
  ${cssH4};
`;

const TextIntro = styled(Text)`
  ${cssH6};
  position: relative;
`;
